import { createContext, useState, useContext, useEffect, useCallback } from "react";
import axios from "../api/axios";
import { useNavigate } from "react-router-dom";
import { changePassword, verifyPassword as verifyPasswordApi, deleteAccount as deleteAccountApi } from "../api/profileService";

// Custom event for token updates
const TOKEN_UPDATE_EVENT = "token_update";
const PROFILE_UPDATE_EVENT = "profile_update";

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(Date.now());

  // Create a token update event emitter
  const emitTokenUpdate = useCallback((data) => {
    // Emit the event to the window for any components listening
    const event = new CustomEvent(TOKEN_UPDATE_EVENT, { detail: data });
    window.dispatchEvent(event);
    
    // Use a more targeted approach instead of triggering storage events
    // This prevents page refreshes while still allowing updates
    try {
      // Store current tokens in a separate item just for communication
      localStorage.setItem("current_tokens", data.tokens.toString());
    } catch (error) {
      console.error("Error updating token value:", error);
    }
  }, []);

  async function login(data, captchaToken) {
    data = { ...data, captchaToken };
    const response = await axios.post("/login", JSON.stringify(data), {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });

    storeToken(response.data.token);
    await fetchProfile();
    return response;
  }

  async function signup(data, captchaToken) {
    data = { 
      ...data, 
      captchaToken,
      // Only include first name and last name
      firstName: data.firstName,
      lastName: data.lastName
    };
    const response = await axios.post("/register", JSON.stringify(data), {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response;
  }

  async function signupPassword(data, pathname) {
    console.log('the path for singup is ', pathname);
    const response = await axios.post(`verify/${pathname}`, JSON.stringify(data), {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response;
  }

  async function forgot(data, captchaToken) {
    data = { ...data, captchaToken };
    const response = await axios.post("/profile/reset", JSON.stringify(data), {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response;
  }

  function storeToken(token) {
    localStorage.setItem("jwtToken", token);
  }

  function getToken() {
    const returnVal = localStorage.getItem("jwtToken");
    return returnVal ? returnVal : 0;
  }

  async function fetchProfile() {
    // Skip API call if no token exists (user is not logged in)
    const token = getToken();
    if (!token || token === 0) {
      return null;
    }
    
    try {
      const response = await axios.get("/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response?.data?.user) {
        const newProfile = response.data.user;
        const oldProfile = JSON.parse(localStorage.getItem("userInfo")) || {};
        
        // Store the updated profile
        localStorage.setItem("userInfo", JSON.stringify(newProfile));
        
        // Check if tokens have changed
        if (newProfile.tokens !== oldProfile.tokens) {
          // Emit token update event
          emitTokenUpdate({ 
            tokens: newProfile.tokens,
            previousTokens: oldProfile.tokens || 0
          });
        }
        
        setProfile(prevProfile => {
          const hasChanged = !prevProfile || 
            prevProfile.tokens !== newProfile.tokens ||
            JSON.stringify(prevProfile) !== JSON.stringify(newProfile);
          
          if (hasChanged) {
            setLastUpdated(Date.now());
            // Emit profile update event
            const profileEvent = new CustomEvent(PROFILE_UPDATE_EVENT, { detail: newProfile });
            window.dispatchEvent(profileEvent);
            return newProfile;
          }
          return prevProfile;
        });
        
        return newProfile;
      }
      return null;
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("Unauthorized");
        logout();
      }
      return null;
    }
  }

  async function authenticateUser() {
    try {
      const response = await axios.get("/profile", {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      return "user" in response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      return false;
    }
  }

  function getProfile() {
    if (profile) return profile;
    const returnVal = JSON.parse(localStorage.getItem("userInfo"));
    if (returnVal) setProfile(returnVal);
    return returnVal;
  }

  function logout() {
    // Clear the profile state first
    setProfile(null);
    
    // Then clear localStorage items
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("current_tokens");
    
    // Update lastUpdated timestamp to trigger re-renders
    setLastUpdated(Date.now());
    
    // Force a state update to any listening components
    const event = new CustomEvent(TOKEN_UPDATE_EVENT, { 
      detail: { tokens: 0, previousTokens: profile?.tokens || 0, loggedOut: true } 
    });
    window.dispatchEvent(event);
    
    // Finally navigate to home page
    navigate("/");
  }

  async function resetPassword(data, pathname) {
    const response = await axios.post(`/profile/${pathname}`, JSON.stringify(data), {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });
    return response;
  }

  async function updatePassword(currentPassword, newPassword) {
    try {
      const response = await changePassword(currentPassword, newPassword);
      return { success: true, data: response };
    } catch (error) {
      console.error("Error updating password:", error);
      return { 
        success: false, 
        message: error.response?.data?.message || "Failed to update password. Please try again." 
      };
    }
  }

  async function verifyPassword({ password }) {
    try {
      const response = await verifyPasswordApi(password);
      return { success: true, data: response };
    } catch (error) {
      console.error("Error verifying password:", error);
      return { 
        success: false, 
        message: error.response?.data?.message || "Failed to verify password. Please try again." 
      };
    }
  }

  async function deleteAccount(password) {
    try {
      const response = await deleteAccountApi(password);
      // If deletion is successful, log the user out
      logout();
      return { success: true, data: response };
    } catch (error) {
      console.error("Error deleting account:", error);
      return { 
        success: false, 
        message: error.response?.data?.message || "Failed to delete account. Please try again." 
      };
    }
  }

  useEffect(() => {
    const storedProfile = localStorage.getItem("userInfo");
    if (storedProfile) {
      setProfile(JSON.parse(storedProfile));
    }
    
    // Only attempt to fetch profile if there's a token (user is logged in)
    const token = getToken();
    if (token && token !== 0) {
      fetchProfile();
      
      // Set up a polling interval for profile updates, but only for logged-in users
      const intervalId = setInterval(() => {
        // Check if token still exists before making API call
        const currentToken = getToken();
        if (currentToken && currentToken !== 0) {
          fetchProfile();
        }
      }, 30000); // Check every 30 seconds
      
      return () => clearInterval(intervalId);
    }
  }, []);

  let value = {
    getToken,
    getProfile,
    fetchProfile,
    authenticateUser,
    login,
    logout,
    signup,
    signupPassword,
    resetPassword,
    updatePassword,
    verifyPassword,
    deleteAccount,
    forgot,
    lastUpdated,
    TOKEN_UPDATE_EVENT,
    PROFILE_UPDATE_EVENT
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
